/**
 * Parsing and formatting of wall lengths.
 *
 * Lengths are always STORED as a decimal number of inches. These functions
 * exist only for the human boundary.
 *
 * DISPLAY: inches with fractions rounded to the nearest 1/32" (owner,
 * 2026-08-29; originally 1/16 from 2026-07-22). This is the `inches_fraction` value of the
 * `measurement_label_format` business rule and is now the default. The older
 * `feet_inches` and plain `inches` formats are retained for configurability.
 *
 * ENTRY accepts, and normalizes to inches:
 *   94            94"
 *   94.1875       decimal inches (shown back as 94 3/16")
 *   94 3/16       whole + fraction (any denominator; the tape reads 32nds)
 *   94-3/16       same, dash separator
 *   3/16          fraction only
 *   8' 6"         feet-inches (still supported; drawings still use it)
 *   8-6           feet-inches shorthand (no slash, both integers)
 *   4 1/2 + 37 1/4  simple arithmetic (41 3/4")
 *   26 * 2          multiply a measured radius to enter a diameter
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html and extended
 * for fraction entry/display.
 */

/** Denominator for fraction DISPLAY rounding. */
// 32, not 16 (owner, 2026-08-29): the shop reads tapes in 32nds, and their
// 6 31/32 displayed as 7" while storing 6.96875 exactly — a screen quietly
// disagreeing with the stored number. Solved-value surfaces that WANT
// coarseness say so via options.roundToUnits; this constant is only how
// finely the truth is printed.
const FRACTION_DENOMINATOR = 32;

/**
 * An unsigned number, with or without a leading zero: 94, 94.5, .5, 0.5.
 *
 * The leading-decimal alternative is load-bearing: a pattern of `\d+(\.\d+)?`
 * alone matches ".5" at the "5", so a typed ".5" silently became 5" — a wrong
 * measurement accepted, the exact failure this module exists to prevent (owner
 * report, 2026-07-28: a ½" countertop entered as ".5" came back as 5").
 */
const NUMBER = String.raw`(?:\d+(?:\.\d+)?|\.\d+)`;

function gcd(a, b) {
  return b ? gcd(b, a % b) : a;
}

/**
 * Extracts an inches value from a fragment that may contain a fraction
 * ("6 1/2", "1/2") or a plain/decimal number ("6", "6.5"). Returns 0 when
 * nothing numeric is present.
 */
function parseInchesPart(text) {
  const frac = text.match(/(\d+)\s*\/\s*(\d+)/);
  if (frac) {
    const den = Number(frac[2]);
    if (den === 0) return 0;
    const wholeMatch = text.slice(0, frac.index).match(new RegExp(NUMBER));
    const whole = wholeMatch ? Number(wholeMatch[0]) : 0;
    return whole + Number(frac[1]) / den;
  }
  const numeric = text.match(new RegExp(NUMBER));
  return numeric ? Number(numeric[0]) : 0;
}

/**
 * Parses a human-entered length into inches.
 *
 * Returns null for anything unparseable, rather than guessing — CLAUDE.md's
 * "no aliases, fuzzy logic, guessing". A wrong length silently accepted is far
 * worse than a rejected one, because it propagates through the closure solver
 * into every other wall.
 *
 * @returns {number|null} inches, or null
 */
function parseLengthLiteral(value) {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return null;

  // Feet, optionally with trailing inches and/or a fraction.
  const feetMatch = text.match(new RegExp(`(${NUMBER})\\s*(?:'|ft|feet)`));
  if (feetMatch) {
    const feet = Number(feetMatch[1]);
    const inches = parseInchesPart(text.replace(feetMatch[0], ''));
    return feet * 12 + inches;
  }

  // Inches with a fraction: "94 3/16", "94-3/16", "3/16". The slash is what
  // distinguishes this from the feet-inches dash shorthand below.
  const frac = text.match(/(\d+)\s*\/\s*(\d+)/);
  if (frac) {
    const den = Number(frac[2]);
    if (den === 0) return null; // 1/0 is not a measurement
    const wholeMatch = text.slice(0, frac.index).match(new RegExp(NUMBER));
    const whole = wholeMatch ? Number(wholeMatch[0]) : 0;
    return whole + Number(frac[1]) / den;
  }

  // Feet-inches shorthand: 8-6 means 8'6". Only when there is no slash and
  // both sides are integers, so it cannot swallow a fraction like 94-3/16.
  const dash = text.match(/^(\d+)\s*-\s*(\d+)$/);
  if (dash) return Number(dash[1]) * 12 + Number(dash[2]);

  const numeric = text.match(new RegExp(`-?${NUMBER}`));
  return numeric ? Number(numeric[0]) : null;
}

/** Splits a flat expression while leaving fraction slashes and 8-6 intact. */
function splitArithmetic(text, level) {
  const parts = [];
  const operators = [];
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const spaced = /\s/.test(text[index - 1] ?? '') && /\s/.test(text[index + 1] ?? '');
    const matches = level === 'add'
      ? (char === '+' || (char === '-' && spaced))
      : (char === '*' || char === '×' || (char === '/' && spaced));
    if (!matches) continue;
    parts.push(text.slice(start, index).trim());
    operators.push(char === '×' ? '*' : char);
    start = index + 1;
  }
  parts.push(text.slice(start).trim());
  return { parts, operators };
}

function parseArithmeticProduct(text) {
  const { parts, operators } = splitArithmetic(text, 'multiply');
  if (parts.some((part) => !part)) return null;
  const values = parts.map((part) => {
    const nonUnitText = part.replace(/\b(?:inches?|in|feet|ft)\b/g, '');
    if (/[a-z_()[\]{};`]/.test(nonUnitText)) return null;
    return parseLengthLiteral(part);
  });
  if (values.some((value) => !Number.isFinite(value))) return null;
  let total = values[0];
  for (let index = 0; index < operators.length; index += 1) {
    const next = values[index + 1];
    if (operators[index] === '/' && next === 0) return null;
    total = operators[index] === '/' ? total / next : total * next;
  }
  return Number.isFinite(total) ? total : null;
}

/**
 * Parses either one conventional length or a safe, flat arithmetic expression.
 * Operators follow normal precedence. Division/subtraction require surrounding
 * spaces so `1/2`, `94-3/16`, and the legacy `8-6` shorthand remain lengths.
 * No code evaluation or named variables are involved.
 */
export function parseLength(value) {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return null;
  if (!/[+*×]|\s[-/]\s/.test(text)) return parseLengthLiteral(text);
  const { parts, operators } = splitArithmetic(text, 'add');
  if (parts.some((part) => !part)) return null;
  const values = parts.map(parseArithmeticProduct);
  if (values.some((item) => !Number.isFinite(item))) return null;
  let total = values[0];
  for (let index = 0; index < operators.length; index += 1) {
    total = operators[index] === '-' ? total - values[index + 1] : total + values[index + 1];
  }
  return Number.isFinite(total) ? total : null;
}

/**
 * Formats inches for display.
 *
 * @param {number} inches
 * @param {'inches_fraction'|'feet_inches'|'inches'} [format] the measurement_label_format rule
 * @param {object} [options]
 * @param {number} [options.precision] decimal places, for the feet_inches/inches formats
 * @param {number} [options.roundToUnits] round to this step BEFORE formatting —
 *   the owner's 2026-08-09 display rule for every System-32-derived figure:
 *   "designers won't be able to deal with metric so display imperial rounded to
 *   1/4" (but use actuals for placement so the system is always using actuals to
 *   avoid drift)". A PRESENTATION step, applied at the last moment: nothing
 *   stored or solved is ever rounded, and this is an option on the ONE formatter
 *   rather than a second one.
 */
/**
 * Rounds a measurement to the nearest `stepUnits` — ⅛", ¼", whatever the rule
 * says (owner, 2026-08-05: the storage table was rounding to whole inches).
 *
 * Reporting totals to the sixteenth is false precision: they are sums of solved
 * openings, so the last sixteenth is arithmetic, not something anyone measured.
 * Rounding to the whole inch went too far the other way and lost real capacity.
 *
 * The multiply-round-divide is done in the STEP's own units, so a quarter lands
 * exactly on .25 rather than on a float a hair off it — which
 * `formatInchesFraction` would then round to a different thirty-second.
 *
 * A non-positive or unusable step returns the value untouched: rounding to
 * "nothing" must not silently zero a measurement.
 */
export function roundToNearest(inches, stepUnits) {
  if (!Number.isFinite(inches)) return inches;
  if (!Number.isFinite(stepUnits) || stepUnits <= 0) return inches;
  return Math.round(inches / stepUnits) * stepUnits;
}

export function formatLength(inches, format = 'inches_fraction', options = {}) {
  if (!Number.isFinite(inches)) return '';
  const value = options.roundToUnits > 0 ? roundToNearest(inches, options.roundToUnits) : inches;

  if (format === 'inches_fraction') return formatInchesFraction(value);
  if (format === 'inches') return `${round(value, options.precision ?? 0)}"`;
  if (format === 'feet_inches') return formatFeetInches(value, options.precision ?? 0);
  throw new RangeError(`formatLength: unknown format "${format}"`);
}

/**
 * Inches with a 1/32"-rounded fraction (reduced, so exact 16ths render as before):
 *   94        -> 94"
 *   94.1875   -> 94 3/16"
 *   94.5      -> 94 1/2"    (16/32 reduced)
 *   0.1875    -> 3/16"
 */
function formatInchesFraction(inches) {
  const negative = inches < 0;
  const totalThirtySeconds = Math.round(Math.abs(inches) * FRACTION_DENOMINATOR);
  const whole = Math.floor(totalThirtySeconds / FRACTION_DENOMINATOR);
  let numerator = totalThirtySeconds % FRACTION_DENOMINATOR;
  const sign = negative ? '-' : '';

  if (numerator === 0) return `${sign}${whole}"`;

  let denominator = FRACTION_DENOMINATOR;
  const divisor = gcd(numerator, denominator);
  numerator /= divisor;
  denominator /= divisor;

  if (whole === 0) return `${sign}${numerator}/${denominator}"`;
  return `${sign}${whole} ${numerator}/${denominator}"`;
}

/**
 * Feet-inches: 15 -> 1'-3", 72 -> 6'-0", 5 -> 5". Retained for the alternate
 * rule value; matches the labels in the owner's original Excalidraw templates.
 */
function formatFeetInches(inches, precision) {
  const negative = inches < 0;
  const total = Math.abs(inches);
  const feet = Math.floor(total / 12);
  let displayInches = round(total - feet * 12, precision);
  let displayFeet = feet;

  if (displayInches >= 12) {
    displayFeet += Math.floor(displayInches / 12);
    displayInches = round(displayInches % 12, precision);
  }

  const sign = negative ? '-' : '';
  if (displayFeet === 0) return `${sign}${displayInches}"`;
  return `${sign}${displayFeet}'-${displayInches}"`;
}

function round(value, precision) {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}
